import React from 'react';
import { useHistory } from 'react-router-dom';

import { formatDate } from "utils";

import { BackendJob } from "components/EditJob";
import { JobStatus } from "components/JobBoard";

import {
    JobContainer,
    TitlesContainer,
    Title,
    Subtitle,
    NewCandidatesInfo,
    PersonOutlineIconStyled,
    JobInfoContainer,
    JobInfoItem,
    CalendarTodayOutlinedIconStyled,
    StartingDate,
    PersonOutlineIconInColorStyled,
    HourglassEmptyOutlinedIconStyled,
    PendingJobInfo,
    CandidateInfo,
    LocationOnOutlinedIconStyled,
    Badge,
    RepostButton,
    JobDetailsContainer
} from './JobList.styles'

interface JobListItemProps {
    job: BackendJob,
    onJobClick: (job: BackendJob) => void,
    isActive?: boolean
}

const JobListItem = ({ job, onJobClick, isActive }: JobListItemProps) => {
    const {
        title,
        id,
        location_address,
        newCandidates,
        startDate,
        total_applications = 0,
        status
    } = job;

    const history = useHistory();

    const handleRepost = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        history.push({
            pathname: 'editJobFirstStep',
            state: {
                mode: 'repost',
                originalJob: job,
            },
        });
    };

    const PendingBadge = (
        <PendingJobInfo>
            <HourglassEmptyOutlinedIconStyled />
            <span>In Review</span>
        </PendingJobInfo>
    );

    const NewCandidatesBadge = newCandidates ? (
        <NewCandidatesInfo>
            <PersonOutlineIconStyled />
            <span>{newCandidates} New</span>
        </NewCandidatesInfo>
    ) : null;

    return (
        <JobContainer
            key={id}
            onClick={() => onJobClick(job)}
            className='no-fastclick'
            $isActive={isActive}
            id={`job-card-${id}`}
            data-testid={`job-card-${id}`}
            aria-label={`job-card-${id}`}
            role="article"
            content-desc={`job-card-${id}`}
        >
            <JobDetailsContainer>
                <TitlesContainer>
                    <div>
                        <Title
                            id={`job-title-${id}`}
                            data-testid={`job-title-${id}`}
                            aria-label={`job-title-${id}`}
                            content-desc={`job-title-${id}`}
                        >{title}</Title>
                    </div>
                    {status === JobStatus.posted ? PendingBadge : NewCandidatesBadge}
                </TitlesContainer>
                <JobInfoContainer>
                    <JobInfoItem>
                        <LocationOnOutlinedIconStyled />
                        <Subtitle
                            id={`job-location-${id}`}
                            data-testid={`job-location-${id}`}
                            aria-label={`job-location-${id}`}
                            content-desc={`job-location-${id}`}
                        > {location_address}</Subtitle>
                    </JobInfoItem>
                    <JobInfoItem>
                        <CalendarTodayOutlinedIconStyled />
                        <StartingDate
                            id={`job-startdate-${id}`}
                            data-testid={`job-startdate-${id}`}
                            aria-label={`job-startdate-${id}`}
                            content-desc={`job-startdate-${id}`}
                        >Starting: {formatDate(startDate as string)}</StartingDate>
                    </JobInfoItem>
                    <JobInfoItem>
                        <PersonOutlineIconInColorStyled />
                        <CandidateInfo
                            id={`job-candidates-${id}`}
                            data-testid={`job-candidates-${id}`}
                            aria-label={`job-candidates-${id}`}
                            content-desc={`job-candidates-${id}`}
                        >{total_applications} {total_applications <= 1 ? 'Candidate' : 'Candidates'}</CandidateInfo>
                    </JobInfoItem>
                </JobInfoContainer>
            </JobDetailsContainer>
            
            {!isActive && (
                <RepostButton
                    variant="contained"
                    color="primary"
                    onClick={handleRepost}
                    id={`job-repost-${id}`}
                    data-testid={`job-repost-${id}`}
                    aria-label={`job-repost-${id}`}
                    content-desc={`job-repost-${id}`}
                >
                    Repost This Job
                </RepostButton>
            )}
        </JobContainer>
    )
}

export default JobListItem;
